import { supabase } from '@/lib/supabase'
import type {
  InvestmentCalculation,
  DilutionImpact,
  InvestmentFormData,
  SecondaryTransactionFormData
} from '@/types/investment'

export class InvestmentCalculator {
  /**
   * Calculate shares to be issued for an investment
   */
  static calculateSharesFromInvestment(
    investmentAmount: number,
    pricePerShare: number
  ): number {
    if (pricePerShare <= 0) {
      throw new Error('Price per share must be greater than 0')
    }
    return Math.floor(investmentAmount / pricePerShare)
  }

  /**
   * Calculate price per share from valuation
   */
  static calculatePricePerShare(
    preMoneyValuation: number,
    totalSharesOutstanding: number
  ): number {
    if (totalSharesOutstanding <= 0) {
      throw new Error('Total shares outstanding must be greater than 0')
    }
    return preMoneyValuation / totalSharesOutstanding
  }

  /**
   * Calculate post-money valuation
   */
  static calculatePostMoney(
    preMoneyValuation: number,
    totalInvestment: number
  ): number {
    return preMoneyValuation + totalInvestment
  }

  /**
   * Calculate ownership percentage after investment
   */
  static calculateOwnershipPercentage(
    shares: number,
    totalSharesAfterInvestment: number
  ): number {
    if (totalSharesAfterInvestment <= 0) return 0
    return (shares / totalSharesAfterInvestment) * 100
  }

  /**
   * Calculate dilution impact for all shareholders
   */
  static async calculateDilutionImpact(
    projectId: string,
    newShares: number,
    excludeUserId?: string
  ): Promise<DilutionImpact[]> {
    // Get current cap table
    const { data: capTable, error } = await supabase
      .from('cap_table_entries')
      .select(`
        *,
        users (id, first_name, last_name, email)
      `)
      .eq('project_id', projectId)

    if (error) throw error

    const currentTotalShares = capTable?.reduce((sum, entry) => sum + entry.shares, 0) || 0
    const newTotalShares = currentTotalShares + newShares

    return capTable?.map(entry => {
      const shareholderName = entry.users
        ? `${entry.users.first_name} ${entry.users.last_name}`
        : 'External Investor'

      const previousPercentage = (entry.shares / currentTotalShares) * 100
      const newPercentage = (entry.shares / newTotalShares) * 100
      const dilutionPercentage = previousPercentage - newPercentage

      return {
        shareholderId: entry.user_id || entry.id,
        shareholderName,
        previousShares: entry.shares,
        previousPercentage,
        newShares: entry.shares, // Their shares don't change
        newPercentage,
        dilutionPercentage
      }
    }) || []
  }

  /**
   * Calculate full investment details including dilution
   */
  static async calculateInvestmentDetails(
    projectId: string,
    fundingRoundId: string,
    investmentAmount: number,
    pricePerShare?: number
  ): Promise<InvestmentCalculation> {
    // Get funding round details
    const { data: fundingRound, error: frError } = await supabase
      .from('funding_rounds')
      .select('*')
      .eq('id', fundingRoundId)
      .single()

    if (frError) throw frError

    // Get current total shares
    const { data: capTable, error: ctError } = await supabase
      .from('cap_table_entries')
      .select('shares')
      .eq('project_id', projectId)

    if (ctError) throw ctError

    const currentTotalShares = capTable?.reduce((sum, entry) => sum + entry.shares, 0) || 0

    // Calculate price per share if not provided
    const preMoney = fundingRound.pre_money_valuation || 0
    const sharePrice = pricePerShare || this.calculatePricePerShare(
      preMoney,
      currentTotalShares
    )

    // Calculate shares to issue
    const sharesIssued = this.calculateSharesFromInvestment(investmentAmount, sharePrice)

    // Calculate valuations
    const postMoney = this.calculatePostMoney(
      preMoney,
      investmentAmount
    )

    // Calculate ownership percentage for new investor
    const totalSharesAfter = currentTotalShares + sharesIssued
    const ownershipPercentage = this.calculateOwnershipPercentage(
      sharesIssued,
      totalSharesAfter
    )

    // Calculate dilution impact
    const dilutionImpact = await this.calculateDilutionImpact(
      projectId,
      sharesIssued
    )

    return {
      sharePrice,
      sharesIssued,
      preMoney: preMoney,
      postMoney,
      ownershipPercentage,
      dilutionImpact
    }
  }

  /**
   * Validate investment against share authorization limits
   */
  static async validateShareAuthorization(
    projectId: string,
    equityClassId: string,
    sharesToIssue: number
  ): Promise<{ valid: boolean; message?: string }> {
    // Get equity class info
    const { data: equityClass, error: ecError } = await supabase
      .from('equity_classes')
      .select('class_name')
      .eq('id', equityClassId)
      .single()

    if (ecError) throw ecError

    // Get project share authorization
    const { data: project, error: pError } = await supabase
      .from('projects')
      .select('total_shares_authorized, authorized_shares')
      .eq('id', projectId)
      .single()

    if (pError) throw pError

    // Get current issued shares for this equity class
    const { data: issuedShares, error: isError } = await supabase
      .from('cap_table_entries')
      .select('shares')
      .eq('project_id', projectId)
      .eq('equity_class_id', equityClassId)

    if (isError) throw isError

    const currentIssued = issuedShares?.reduce((sum, entry) => sum + entry.shares, 0) || 0

    // Check authorization
    const authorized = project.total_shares_authorized || project.authorized_shares || 0

    const remainingShares = authorized - currentIssued
    
    if (sharesToIssue > remainingShares) {
      return {
        valid: false,
        message: `Insufficient authorized shares. Available: ${remainingShares.toLocaleString()}, Requested: ${sharesToIssue.toLocaleString()}`
      }
    }

    return { valid: true }
  }

  /**
   * Calculate secondary transaction details
   */
  static async calculateSecondaryTransaction(
    sellerId: string,
    equityClassId: string,
    sharesToSell: number,
    pricePerShare: number,
    taxWithholdingRate: number = 0
  ): Promise<{
    availableShares: number
    totalSaleAmount: number
    taxWithholding: number
    netProceeds: number
    canProceed: boolean
    message?: string
  }> {
    // Get seller's vested shares
    const { data: offers, error } = await supabase
      .from('equity_offers')
      .select('*')
      .eq('user_id', sellerId)
      .eq('equity_class_id', equityClassId)
      .eq('status', 'accepted')

    if (error) throw error

    // Calculate total vested shares
    let availableShares = 0
    const today = new Date()

    for (const offer of offers || []) {
      const sharesOffered = offer.shares_offered || 0
      if (!offer.vesting_schedule_id) {
        // No vesting schedule means immediate
        availableShares += sharesOffered
      } else {
        // Calculate vested portion based on offer_date
        const vestingStart = new Date(offer.offer_date)
        const monthsVested = Math.floor(
          (today.getTime() - vestingStart.getTime()) / (1000 * 60 * 60 * 24 * 30)
        )
        // Default to 4-year monthly vesting if schedule exists
        const monthlyShares = sharesOffered / 48
        availableShares += Math.floor(monthlyShares * Math.min(monthsVested, 48))
      }
    }

    // Check for previous sales
    const { data: previousSales, error: salesError } = await supabase
      .from('secondary_transactions')
      .select('shares_sold')
      .eq('seller_id', sellerId)
      .eq('equity_class_id', equityClassId)
      .in('transaction_status', ['approved', 'completed'])

    if (salesError) throw salesError

    const previouslySold = previousSales?.reduce((sum, sale) => sum + sale.shares_sold, 0) || 0
    availableShares -= previouslySold

    // Calculate transaction amounts
    const totalSaleAmount = sharesToSell * pricePerShare
    const taxWithholding = totalSaleAmount * (taxWithholdingRate / 100)
    const netProceeds = totalSaleAmount - taxWithholding

    const canProceed = sharesToSell <= availableShares

    return {
      availableShares,
      totalSaleAmount,
      taxWithholding,
      netProceeds,
      canProceed,
      message: canProceed 
        ? undefined 
        : `Insufficient vested shares. Available: ${availableShares.toLocaleString()}, Requested: ${sharesToSell.toLocaleString()}`
    }
  }

  /**
   * Parse CSV data for bulk investment import
   */
  static parseInvestmentCSV(csvContent: string): InvestmentFormData[] {
    const lines = csvContent.trim().split('\n')
    const headers = lines[0].split(',').map(h => h.trim())
    
    const investments: InvestmentFormData[] = []

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim())
      const row: any = {}
      
      headers.forEach((header, index) => {
        row[header] = values[index]
      })

      investments.push({
        investor_name: row.investor_name,
        investor_email: row.investor_email,
        investor_type: (row.investor_type?.toLowerCase() || 'other') as any,
        investment_amount: parseFloat(row.investment_amount.replace(/[^0-9.-]+/g, '')),
        equity_class_id: '', // Will be mapped later
        price_per_share: 0, // Will use funding round price
        is_lead_investor: row.is_lead_investor?.toLowerCase() === 'true',
        investment_date: row.investment_date || new Date().toISOString().split('T')[0],
        notes: row.notes
      })
    }

    return investments
  }

  /**
   * Format currency
   */
  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  /**
   * Format percentage
   */
  static formatPercentage(value: number, decimals: number = 2): string {
    return `${value.toFixed(decimals)}%`
  }
}